<!-- MilestoneRow.vue -->
<template>
  <div>
    <!-- Milestone Row -->
    <div
      :class="[
        'grid items-center px-2 py-1.5 border-t border-gray-200 cursor-pointer',
        { 'bg-keppel text-white': expanded }
      ]"
      style="grid-template-columns: 1.55fr 0.8fr 1.45fr 1.35fr 1.15fr 1.15fr;"
      @click="toggle"
    >
      <a
        :href="acceloLink('milestone', milestone.id)"
        target="_blank"
        rel="noopener noreferrer"
        class="font-medium hover:text-cerulean focus:outline-none focus:underline"
      >
        {{ milestone.title }}
      </a>
      <div>
        <StatusBadge :status="milestone.standing" />
      </div>

      <!-- Time Allocated (aggregated) -->
      <div class="pr-3">
        <div class="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
          <div
            class="absolute top-0 left-0 h-4 rounded-l bg-sushi"
            :style="{ width: milestoneProgress.usedPct + '%' }"
          />
          <div
            class="absolute top-0 right-0 h-4 rounded-r bg-cerulean"
            :style="{ width: milestoneProgress.remainingPct + '%' }"
          />
          <div
            class="absolute top-0 right-0 h-4 rounded-r bg-red-500"
            :style="{ width: milestoneProgress.overPct + '%' }"
          />
          <div
            class="absolute inset-0 flex items-center justify-center text-xs font-medium text-white select-none"
          >
            {{ formatUsagePercent(milestoneLogged, milestoneBudget) }}
          </div>
        </div>
      </div>

      <div class="flex items-center gap-1 pl-4 text-sm font-medium text-slate-700 tabular-nums">
        <span
          class="inline-block text-right"
          style="width: 7ch;"
        >
          {{ formatDuration(milestoneLogged) }}
        </span>
        <span>/</span>
        <span
          class="inline-flex items-center justify-center rounded-sm px-2 py-0.5 text-xs font-semibold leading-none text-white"
          :style="budgetBadgeStyle(milestoneLogged, milestoneBudget)"
        >
          {{ formatDuration(milestoneBudget) }}
        </span>
      </div>

      <!-- ✅ Start/End driven by picker selection (with fallbacks) -->
      <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ startLabel }}</div>
      <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ endLabel }}</div>
    </div>

    <!-- Tasks under this milestone -->
    <transition name="fade">
      <div v-if="expanded">
        <transition-group
          name="fade"
          tag="div"
        >
          <div
            v-for="task in filteredTasks"
            :key="task.id"
            class="grid items-center px-2 py-1.5 hover:bg-gray-50"
            style="grid-template-columns: 1.55fr 0.8fr 1.45fr 1.35fr 1.15fr 1.15fr;"
          >
            <div
              class="pr-2 min-w-0 flex flex-col gap-1"
              style="padding-left: 2em"
            >
              <a
                :href="acceloLink('task', task.id)"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 hover:text-cerulean"
              >
                <span class="inline-block w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />
                <span class="text-sm font-semibold text-gray-800 truncate inline-block max-w-full">{{ task.title }}</span>
              </a>
              <div
                v-if="assigneeMember(task)"
                class="pl-4 flex flex-wrap items-center gap-2"
              >
                <RouterLink
                  v-if="assigneeMember(task)?.id"
                  :to="assigneeLink(task)"
                  class="px-2 py-0.5 rounded-full text-xs font-semibold bg-cerulean text-white border border-cerulean hover:bg-white hover:text-cerulean"
                  @click.stop
                >
                  {{ assigneeMember(task)?.name }}
                </RouterLink>
                <span
                  v-else
                  class="px-2 py-0.5 rounded-full text-xs font-semibold bg-cerulean text-white border border-cerulean"
                >
                  {{ assigneeMember(task)?.name }}
                </span>
              </div>
            </div>
            <div>
              <StatusBadge :status="task.standing" />
            </div>
            <div class="pr-3">
              <div class="relative w-full h-4 bg-gray-200 rounded overflow-hidden">
                <div
                  class="absolute top-0 left-0 h-4 rounded-l bg-sushi"
                  :style="{ width: taskProgress(task).usedPct + '%' }"
                />
                <div
                  class="absolute top-0 right-0 h-4 rounded-r bg-cerulean"
                  :style="{ width: taskProgress(task).remainingPct + '%' }"
                />
                <div
                  class="absolute top-0 right-0 h-4 rounded-r bg-red-500"
                  :style="{ width: taskProgress(task).overPct + '%' }"
                />
                <div
                  class="absolute inset-0 flex items-center justify-center text-xs font-medium text-white select-none"
                >
                  {{ formatUsagePercent(taskLogged(task), taskBudget(task)) }}
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1 pl-4 text-sm font-medium text-slate-700 tabular-nums">
              <span
                class="inline-block text-right"
                style="width: 7ch;"
              >
                {{ formatDuration(taskLogged(task)) }}
              </span>
              <span>/</span>
              <span
                class="inline-flex items-center justify-center rounded-sm px-2 py-0.5 text-xs font-semibold leading-none text-white"
                :style="budgetBadgeStyle(taskLogged(task), taskBudget(task))"
              >
                {{ formatDuration(taskBudget(task)) }}
              </span>
            </div>
            <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ formatDate(task.dateCommenced) }}</div>
            <div class="text-sm font-medium text-slate-700 whitespace-nowrap">{{ formatDate(task.dateDue) }}</div>
          </div>
        </transition-group>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useProjectManagementStore } from '@/pinia/projectManagementStore'
import StatusBadge from './StatusBadge.vue'

export default {
  name: 'MilestoneRow',
  components: { RouterLink, StatusBadge },
  props: {
    milestone: { type: Object, required: true },
    tasks: { type: Array, default: null },
    expandAll: { type: Boolean, default: false }
  },
  setup(props) {
    const store = useProjectManagementStore()
    const expanded = ref(props.expandAll)
    const toggle = () => (expanded.value = !expanded.value)

    watch(
      () => props.expandAll,
      (value) => {
        expanded.value = value
      }
    )

    const filteredTasks = computed(() =>
      props.tasks ?? store.milestoneTasks(props.milestone.id)
    )

    const taskLogged = (task) => Number(task?.logged ?? task?.billable) || 0
    const taskBudget = (task) => Number(task?.objectBudget ?? task?.object_budget ?? task?.remaining) || 0

    const milestoneRollupLogged = computed(() => Number(
      props.milestone?.loggedSubtotalSeconds ??
      props.milestone?.logged_subtotal ??
      props.milestone?.milestoneObjectBudget?.loggedSubtotalSeconds ??
      props.milestone?.milestoneObjectBudget?.loggedSubtotal ??
      props.milestone?.milestoneObjectBudget?.logged_subtotal ??
      props.milestone?.milestone_object_budget?.loggedSubtotalSeconds ??
      props.milestone?.milestone_object_budget?.logged_subtotal ??
      0
    ))

    const milestoneRollupBudget = computed(() => Number(
      props.milestone?.serviceTimeSubtotalEstimateSeconds ??
      props.milestone?.service_time_subtotal_estimate ??
      props.milestone?.milestoneObjectBudget?.serviceTimeSubtotalEstimateSeconds ??
      props.milestone?.milestoneObjectBudget?.serviceTimeSubtotalEstimate ??
      props.milestone?.milestoneObjectBudget?.service_time_subtotal_estimate ??
      props.milestone?.milestone_object_budget?.serviceTimeSubtotalEstimateSeconds ??
      props.milestone?.milestone_object_budget?.service_time_subtotal_estimate ??
      0
    ))

    const progressSegments = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      if (logged <= 0 && budget <= 0) {
        return { usedPct: 0, remainingPct: 0, overPct: 0 }
      }
      if (logged > budget && logged > 0) {
        return {
          usedPct: Math.round((budget / logged) * 100),
          remainingPct: 0,
          overPct: Math.round(((logged - budget) / logged) * 100)
        }
      }
      if (budget <= 0) {
        return { usedPct: 0, remainingPct: 0, overPct: 0 }
      }
      const usedPct = Math.max(0, Math.min(100, Math.round((logged / budget) * 100)))
      return { usedPct, remainingPct: 100 - usedPct, overPct: 0 }
    }
    const taskProgress = (task) => progressSegments(taskLogged(task), taskBudget(task))
    const budgetBadgeStyle = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      return {
        backgroundColor: logged > budget ? '#f06464' : '#22a85a',
        minWidth: '6.5ch'
      }
    }

    const milestoneLogged = computed(() => {
      if (Number.isFinite(milestoneRollupLogged.value) && milestoneRollupLogged.value > 0) {
        return milestoneRollupLogged.value
      }

      return filteredTasks.value.reduce((sum, task) => sum + taskLogged(task), 0)
    })
    const milestoneBudget = computed(() => {
      if (Number.isFinite(milestoneRollupBudget.value) && milestoneRollupBudget.value > 0) {
        return milestoneRollupBudget.value
      }

      return filteredTasks.value.reduce((sum, task) => sum + taskBudget(task), 0)
    })

    const milestoneProgress = computed(() => progressSegments(milestoneLogged.value, milestoneBudget.value))

    const formatDuration = secs => {
      const s = Number(secs) || 0
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }
    const formatUsagePercent = (loggedValue, budgetValue) => {
      const logged = Number(loggedValue) || 0
      const budget = Number(budgetValue) || 0
      if (budget <= 0) return '--'
      return `${Math.round((logged / budget) * 100)}%`
    }
    const formatDate = epoch => (epoch ? new Date(epoch * 1000).toLocaleDateString() : '-')

    const assigneeMember = (task) => {
      const assignee = task?.assignee
      if (!assignee) return null
      if (typeof assignee === 'string') {
        const trimmed = assignee.trim()
        return trimmed ? { id: null, name: trimmed } : null
      }
      const idRaw = assignee.id ?? assignee.Id
      const id = Number(idRaw)
      const first = assignee.firstname || assignee.firstName || assignee.FirstName || ''
      const last = assignee.surname || assignee.lastName || assignee.LastName || ''
      const full = `${first} ${last}`.trim()
      const display = full || assignee.name || assignee.Name || assignee.email || assignee.Email
      if (!display) return null
      return { id: Number.isFinite(id) ? id : null, name: display }
    }

    const assigneeLink = (task) => {
      const member = assigneeMember(task)
      if (!member?.id) return null
      return { name: 'EmployeeDashboard', params: { staffId: member.id } }
    }

    const acceloLink = (type, id) => `https://perbyte.accelo.com/?action=view_${type}&id=${id}`

    const defaultSel = { startKey: 'date_planned_start', endKey: 'date_planned_due' }

    const fields = computed(() => {
      const jobId = Number(props.milestone.job)
      if (typeof store.scheduleFieldsForJob === 'function') {
        return store.scheduleFieldsForJob(jobId) || defaultSel
      }
      return defaultSel
    })

    const snakeToCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    const toEpoch = (v) => {
      if (v === null || v === undefined || v === '') return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    }
    const human = (epoch) => (epoch ? new Date(epoch * 1000).toLocaleDateString() : '-')

    const getScheduleDate = (milestone, key) => {
      const s = (milestone && (milestone.milestoneObjectSchedule || milestone.milestone_object_schedule)) || {}
      const camel = snakeToCamel(key)
      const val = s[camel] !== undefined ? s[camel] : s[key]
      return toEpoch(val)
    }

    const startEpoch = computed(() =>
      getScheduleDate(props.milestone, fields.value.startKey) ??
      toEpoch(props.milestone.dateStarted) ??
      toEpoch(props.milestone.dateCommenced)
    )
    const endEpoch = computed(() =>
      getScheduleDate(props.milestone, fields.value.endKey) ??
      toEpoch(props.milestone.dateDue) ??
      toEpoch(props.milestone.dateCompleted)
    )

    const startLabel = computed(() => human(startEpoch.value))
    const endLabel = computed(() => human(endEpoch.value))

    return {
      expanded,
      toggle,
      filteredTasks,
      taskLogged,
      taskBudget,
      taskProgress,
      budgetBadgeStyle,
      milestoneBudget,
      milestoneLogged,
      milestoneProgress,
      formatDuration,
      formatUsagePercent,
      formatDate,
      assigneeMember,
      assigneeLink,
      acceloLink,
      startLabel,
      endLabel
    }
  }
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
